<div class="video-module-container" [ngClass]="{'theme-dark': isDarkMode}">
  <div class="academic-accent"></div>
  <mat-toolbar color="primary" class="video-module-header">
    <button mat-icon-button (click)="goBack()">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <span>Video Generation</span>
    <span class="spacer"></span>
    <button mat-icon-button [matTooltip]="'Help'" (click)="openVideoModuleGuide()">
      <mat-icon>help_outline</mat-icon>
    </button>
    <button mat-icon-button [matTooltip]="isDarkMode ? 'Light Mode' : 'Dark Mode'" (click)="toggleDarkMode()">
      <mat-icon>{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</mat-icon>
    </button>
    <button mat-icon-button [matTooltip]="'Reset Settings'" (click)="resetSettings()">
      <mat-icon>refresh</mat-icon>
    </button>
  </mat-toolbar>

  <!-- Global Loader -->
  <div class="global-loader" *ngIf="isGenerating">
    <div class="loader-content">
      <div class="spinner-container">
        <mat-progress-spinner mode="determinate" [value]="processingProgress" diameter="48"></mat-progress-spinner>
        <div class="progress-percentage">{{ processingProgress | number:'1.0-0' }}%</div>
      </div>
      <div class="loader-text">
        <h3>Generating Video</h3>
        <p>{{ processingStep }}</p>
        <div class="time-info">
          <span>Time remaining: {{ formatTime(remainingTime) }}</span>
          <span>Total time: {{ formatTime(estimatedTime) }}</span>
        </div>
        <div class="typing-animation">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
      <div class="progress-indicator">
        <mat-progress-bar mode="determinate" [value]="processingProgress"></mat-progress-bar>
      </div>
      <div class="cancel-action" *ngIf="processingProgress < 100">
        <button mat-button color="warn" (click)="cancelGeneration()">Cancel</button>
      </div>
    </div>
  </div>

  <div class="content-container">
    <!-- Colab URL Setup Card -->
    <mat-card class="setup-card" *ngIf="showColabUrlInput">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>settings</mat-icon> Colab API Setup
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <p class="setup-description">Please enter the URL of your Colab API to enable video generation:</p>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Colab API URL</mat-label>
          <input matInput [formControl]="colabUrlControl" placeholder="https://your-colab-url.gradio.app">
          <mat-error *ngIf="colabUrlControl.invalid">Please enter a valid URL</mat-error>
        </mat-form-field>
      </mat-card-content>
      <mat-card-actions align="end">
        <button mat-button (click)="showColabUrlInput = false">Cancel</button>
        <button mat-raised-button color="primary" (click)="setColabUrl()" [disabled]="!colabUrlControl.valid">
          Set URL
        </button>
      </mat-card-actions>
    </mat-card>

    <!-- Video Generation Form -->
    <mat-card class="generation-card" *ngIf="!showColabUrlInput">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>movie</mat-icon> Video Generation
        </mat-card-title>
      </mat-card-header>
      
      <mat-card-content>
        <form [formGroup]="videoForm" class="video-form">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Prompt</mat-label>
            <textarea matInput formControlName="prompt" rows="3" 
              placeholder="Describe the video you want to generate..."
              [disabled]="isGenerating"></textarea>
            <mat-hint>
              <span>Be descriptive and specific about what you want in the video</span>
              <a class="tips-link" (click)="openPromptGuide()">
                <mat-icon>help_outline</mat-icon> Tips for better results
              </a>
            </mat-hint>
          </mat-form-field>

          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Video Length (frames)</mat-label>
              <input matInput type="number" formControlName="video_length" 
                min="16" max="256" [disabled]="isGenerating">
              <mat-hint>Number of frames (16-256)</mat-hint>
            </mat-form-field>
          </div>
        </form>
      </mat-card-content>

      <mat-card-actions align="end">
        <button mat-button (click)="resetForm()" [disabled]="isGenerating">
          Reset
        </button>
        <div class="generation-info" *ngIf="!isGenerating && estimatedTime > 0">
          <mat-icon>timer</mat-icon>
          <span>Estimated time: {{ formatTime(estimatedTime) }}</span>
        </div>
        <button mat-raised-button color="primary" 
          (click)="generateVideo()" 
          [disabled]="!videoForm.valid || isGenerating">
          {{ isGenerating ? 'Generating...' : 'Generate Video' }}
        </button>
      </mat-card-actions>

      <mat-card-footer>
        <mat-error *ngIf="errorMessage" class="error-message">
          {{ errorMessage }}
        </mat-error>
      </mat-card-footer>
    </mat-card>

    <!-- Video Display -->
    <mat-card class="video-display-card" *ngIf="videoGenerated && !videoLoadError">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>play_circle</mat-icon> Generated Video
        </mat-card-title>
      </mat-card-header>
      
      <mat-card-content>
        <div class="video-container">
          <video #videoPlayer controls [src]="safeVideoUrl" crossorigin="anonymous" (error)="handleVideoError($event)">
            Your browser does not support the video tag.
          </video>
        </div>
      </mat-card-content>

      <mat-card-actions align="end">
        <button mat-button (click)="regenerateVideo()">
          Generate Another
        </button>
        <button mat-raised-button color="primary" (click)="downloadVideo()">
          <mat-icon>download</mat-icon> Download Video
        </button>
      </mat-card-actions>
    </mat-card>

    <!-- Video Load Error -->
    <mat-card class="error-card" *ngIf="videoLoadError">
      <mat-card-header>
        <mat-card-title>
          <mat-icon color="warn">error</mat-icon> Video Generation Error
        </mat-card-title>
      </mat-card-header>
      
      <mat-card-content>
        <p>
          We encountered an error while generating your video. This could be due to temporary issues with the server.
          Please try again or use a different prompt.
        </p>
      </mat-card-content>

      <mat-card-actions align="end">
        <button mat-raised-button color="primary" (click)="regenerateVideo()">
          Try Again
        </button>
      </mat-card-actions>
    </mat-card>
  </div>

  <!-- Change Colab URL -->
  <div class="change-url">
    <button mat-button (click)="showColabUrlInput = true">
      <mat-icon>settings</mat-icon> Change Colab API URL
    </button>
  </div>
</div> 